import { ninetyDeg, twoSeventyDeg } from '@baseValues'
import { useAppStore } from '@store'
import { Acabados, Hole, Item, LinkedItem, Types } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { Box3, Euler, Matrix4, Mesh, Object3D, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { ITEM_SCENE_SCALE } from '@constants'
import getMorphsFromGLB from '../../../store/getMorphsFromGLB'
import { getZonesFromGLB } from '../getZonesFromGLB'
import { getGLBSize } from './getGLBSize'
import { isKubeCushion, isKubeModule } from './rinconeroUtils'

type Props = {
  id: string
  glbData: any
  type: Types
  catalogID: string
  visible?: boolean
  icono?: string
  matrix?: Matrix4 | undefined | null
  backGLBInfo: import('../../../types').BackGLBInfo
  extraInfo: { zonas: { idAcabadoUnidad: number; zona: string }[] }
  img?: string
  shouldGoToBackHeight?: boolean
  /** Sustitución: conservar x/y/z de la matriz (no altura del backend). */
  preserveMatrixPosition?: boolean
  itemInfo: Record<string, unknown>
}

/**
 * Tamaño del GLB considerando SOLO los meshes visibles (la espuma que se ve),
 * excluyendo helpers, anclas o cajas de colisión invisibles que el GLB pueda
 * incluir. `Box3.setFromObject` engloba también objetos invisibles y puede
 * sobredimensionar la pieza, lo que dejaba huecos entre unidades kube colocadas
 * por ancho. Devuelve null si no hay meshes visibles (para hacer fallback).
 */
export function getVisibleMeshBounds(root: Object3D | undefined | null): {
  size: Vector3
  min: Vector3
  max: Vector3
} | null {
  if (!root) return null
  const box = new Box3()
  let hasVisibleMesh = false
  root.traverse((obj) => {
    const mesh = obj as Mesh
    if (!mesh.isMesh) return
    // Saltar cualquier objeto marcado como no visible (cajas de colisión, etc.).
    let node: Object3D | null = obj
    let visible = true
    while (node) {
      if (node.visible === false) {
        visible = false
        break
      }
      node = node.parent
    }
    if (!visible) return
    box.expandByObject(mesh)
    hasVisibleMesh = true
  })
  if (!hasVisibleMesh || box.isEmpty()) return null
  return {
    size: box.getSize(new Vector3()),
    min: box.min.clone(),
    max: box.max.clone(),
  }
}

function getVisibleMeshSize(root: Object3D | undefined | null): Vector3 | null {
  return getVisibleMeshBounds(root)?.size ?? null
}

export function newGetDataFromGLB({
  glbData,
  icono,
  catalogID,
  visible,
  matrix,
  id,
  type,
  backGLBInfo,
  extraInfo,
  img,
  itemInfo,
  shouldGoToBackHeight,
  preserveMatrixPosition = false,
  ...rest
}: Props) {
  const {
    idUnidades,
    referenciaBlender,
    descripcion,
    descripcionLarga,
    ancho,
    fondo,
    alto,
    mano,
    altura,
    tipoNormalizado,
    tipoUnidNorm,
    universal,
    precio,
    acabados,
    referenciaJpgpng,
    // campos personalizados no están en el tipo, se accede por string
  } = backGLBInfo

  const armazonBlancoFromBackend = (backGLBInfo as any)['armazonBlanco']
  const bisagra = (backGLBInfo as any)['bisagra']
  const idCambioArmazon = (backGLBInfo as any)['idCambioArmazon']
  const idCambioGolaBlanco = (backGLBInfo as any)['idCambioGolaBlanco']
  const idCambioGolaPetroleo = (backGLBInfo as any)['idCambioGolaPetroleo']
  const url = (backGLBInfo as any)['url']
  const paginaPdf = (backGLBInfo as any)['paginaPdf']
  const referencia = (backGLBInfo as any)['referencia']

  // Determinar ArmazonBlanco: priorizar el valor del backend, sino sincronizar con catalogID
  // Si el catalogID tiene prefijo "PE " → es petróleo (false), sino es blanco (true)
  let armazonBlanco: boolean
  if (typeof armazonBlancoFromBackend === 'boolean') {
    // Si viene del backend como booleano válido, usarlo
    armazonBlanco = armazonBlancoFromBackend
  } else {
    // Si no viene del backend o es undefined/null, sincronizar con catalogID
    const catalogIDStr = String(catalogID || '')
    armazonBlanco = !catalogIDStr.startsWith('PE ')
  }

  const { zones, textures } = getZonesFromGLB(
    glbData.scene?.children,
    acabados as Acabados[],
    extraInfo,
  ) || {
    zones: {},
    textures: [],
  }

  const newPosition = toVector3()
  const backendAltura = altura !== null && altura !== undefined ? altura : null
  if (matrix) {
    matrix.decompose(newPosition, toQuaternion(), toVector3())

    if (preserveMatrixPosition) {
      // Sustitución: la unidad nueva ocupa exactamente el mismo sitio que la anterior.
    } else if (!shouldGoToBackHeight) {
      // IMPORTANTE: La altura de colocación (y) SIEMPRE debe venir del backend del nuevo item
      // No debe usar la posición Y calculada de la matriz que viene del item de referencia
      //
      // Diferencia clave:
      // - alto: tamaño de la unidad (800) -> se usa para height del item (SIEMPRE del nuevo item)
      // - altura: altura de colocación (1500) -> se usa para y (posición Y del item) - SIEMPRE del backend
      //
      // Reglas:
      // 1. Si altura tiene valor, usar ese valor
      // 2. Si altura es null y es MBJ, usar 0 (no la posición Y de la matriz)
      // 3. Si altura es null y no es MBJ, usar 0 como fallback
      //
      // La posición X y Z de la matriz se preservan (vienen de getRelativeMatrix para posicionamiento relativo)
      // Pero la posición Y (altura de colocación) siempre debe ser la del backend del nuevo item

      let finalY: number

      if (backendAltura !== null && backendAltura > 0) {
        // Caso 1: Usar altura del backend si tiene valor
        finalY = backendAltura
        // console.log('✅ [newGetDataFromGLB] Usando altura del backend:', finalY.toFixed(2))
      } else if (tipoNormalizado === 'MBJ') {
        // Caso 2: Si es MBJ y altura es null, usar 0 (no la posición Y de la matriz)
        finalY = 0
        // console.log('✅ [newGetDataFromGLB] MBJ con altura null, usando 0 (no posición Y de matriz)')
      } else {
        // Caso 3: Para otros tipos si altura es null, usar 0 como fallback
        finalY = 0
        // console.log('⚠️ [newGetDataFromGLB] Altura null, usando 0 como fallback')
      }

      matrix = matrix.setPosition(newPosition.x, finalY, newPosition.z)
    } else {
      // Si shouldGoToBackHeight es true, usar altura del backend (o 0 si es null y MBJ)
      const finalY =
        backendAltura !== null && backendAltura > 0
          ? backendAltura
          : tipoNormalizado === 'MBJ'
            ? 0
            : 0
      matrix = matrix.setPosition(newPosition.x, finalY, newPosition.z)
    }
  }

  const boundingBox = new OBB().fromBox3(new Box3().setFromObject(glbData?.scene ?? glbData))
  const glbSize = getGLBSize(glbData?.scene ?? glbData)

  // Los módulos kube llegan del backend con ancho/fondo/alto ~10× más pequeños
  // que el modelo 3D real: el GLB se dibuja escalado × ITEM_SCENE_SCALE, de modo
  // que su tamaño visible es (glbSize × ITEM_SCENE_SCALE). Usar las medidas del
  // backend dejaba la caja de colisión 10× menor que la pieza visible, por lo que
  // las unidades se solapaban al dibujarlas y el snap no separaba nada. Derivamos
  // las dimensiones de colisión del tamaño real del GLB para que coincidan con lo
  // que se ve en pantalla. Solo afecta a kube; Atemporal conserva su lógica.
  // Usar el tamaño de los meshes VISIBLES (la espuma real) si está disponible:
  // así la caja de colisión coincide con lo que se ve y las unidades quedan a
  // tope, sin huecos. Si el GLB no expone meshes visibles, fallback a glbSize.
  const kubeVisibleBounds = isKubeModule(catalogID)
    ? getVisibleMeshBounds(glbData?.scene ?? glbData)
    : null
  const kubeVisibleSize = kubeVisibleBounds?.size ?? (isKubeModule(catalogID) ? glbSize : null)
  const kubeCollisionDims = kubeVisibleSize
    ? {
        width: kubeVisibleSize.x * ITEM_SCENE_SCALE,
        height: kubeVisibleSize.y * ITEM_SCENE_SCALE,
        thickness: kubeVisibleSize.z * ITEM_SCENE_SCALE,
      }
    : null
  // Min del mesh visible (local GLB). Sirve para alinear la OBB al sólido real
  // (ancho/fondo/alto) cuando el mesh no arranca en (0,0,0) — clave en respaldos.
  const meshLocalMin = kubeVisibleBounds
    ? {
        x: kubeVisibleBounds.min.x,
        y: kubeVisibleBounds.min.y,
        z: kubeVisibleBounds.min.z,
      }
    : undefined
  // Cojines: asentar espuma en Y respecto al origen.
  const meshLocalYMin = isKubeCushion(catalogID) && meshLocalMin ? meshLocalMin.y : 0

  // Función para obtener el height correcto basado en el acabado de la textura
  const getHeightFromTexture = (textures: any[], defaultHeight: number) => {
    if (descripcion?.toLowerCase().includes('encimera')) {
      const encimeraTexture = textures.find(
        (t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera',
      )
      if (encimeraTexture?.acabado) {
        const acabado = encimeraTexture.acabado.toLowerCase()
        if (acabado.includes('porcelanicas 12')) return 12
        if (acabado.includes('porcelanicas 20')) return 20
        if (acabado.includes('postformadas')) return 30
        if (acabado.includes('compacto')) return 10
      }
    }
    return defaultHeight
  }

  const correctHeight = getHeightFromTexture(textures, alto ?? 0)

  // Reglas para finalY al dibujar (no en sustitución con matriz preservada).
  const matrixPlacementY = matrix
    ? (() => {
        const pos = toVector3()
        matrix.decompose(pos, toQuaternion(), toVector3())
        return pos.y
      })()
    : null
  const finalY =
    preserveMatrixPosition && matrixPlacementY !== null
      ? matrixPlacementY
      : altura !== null && altura !== undefined
        ? altura
        : tipoNormalizado === 'MBJ'
          ? 0
          : 0
  const finalHeight = correctHeight

  if (matrix && !preserveMatrixPosition) {
    const currentPos = toVector3()
    matrix.decompose(currentPos, toQuaternion(), toVector3())
    matrix = matrix.setPosition(currentPos.x, finalY, currentPos.z)
  }

  // console.log('🔍 [newGetDataFromGLB] Propiedades finales:', {
  //   catalogID,
  //   tipoNormalizado,
  //   finalY: finalY.toFixed(2),
  //   finalHeight: finalHeight.toFixed(2),
  //   alto: alto?.toFixed(2),
  //   altura: altura?.toFixed(2),
  //   matrixY: matrix ? newPosition.y.toFixed(2) : 'sin matriz',
  // })

  let props = {
    glbPath: referenciaBlender,
    idUnidades,
    type: type === 'linkedItem' ? ('linkedItem' as const) : type,
    id,
    universal,
    textures,
    snappedWall: null,
    description: descripcion || '',
    price: precio ?? 0,
    y: finalY,
    tipoNormalizado: tipoNormalizado || '',
    tipoUnidNorm: tipoUnidNorm || '',
    image: icono,
    width: kubeCollisionDims ? kubeCollisionDims.width : (ancho ?? 0),
    height: kubeCollisionDims ? kubeCollisionDims.height : finalHeight, // SIEMPRE usar alto del backend del nuevo item
    thickness: kubeCollisionDims ? kubeCollisionDims.thickness : (fondo ?? 0),
    zones,
    boundingBox,
    glbSize,
    catalogID,
    meshLocalYMin,
    meshLocalMin,
    isBlocked: false,
    long: descripcionLarga || '',
    hand: mano || '',
    mano: 'D',
    referencia: referencia ?? '',
    selected: true,
    rotation: (() => {
      if (preserveMatrixPosition && matrix) {
        const quat = toQuaternion()
        matrix.decompose(toVector3(), quat, toVector3())
        return new Euler().setFromQuaternion(quat, 'YXZ').y
      }
      return twoSeventyDeg
    })(),
    url: url ?? '',
    matrix: (() => {
      if (matrix) {
        return matrix
      }
      // Cuando no hay matriz, crear una con rotación de 180 grados para que quede de frente
      // (sin rotación muestra la parte de atrás, 180 grados la muestra de frente)
      // Posición X=1000 para centrar mejor el item en la pantalla
      return new Matrix4().makeRotationY(Math.PI).setPosition(toVector3([1000, altura ?? 0, 0]))
    })(),
    visible: Boolean(visible),
    hasMorphs: Boolean((backGLBInfo as any).morphs),
    morphs: (() => {
      const morphs = getMorphsFromGLB(backGLBInfo)
      // Si es encimera, actualizar el morph "Alto" con el height correcto
      if (descripcion?.toLowerCase().includes('encimera')) {
        return morphs.map((morph) => {
          if (morph.name === 'Alto') {
            return {
              ...morph,
              default: correctHeight,
              _default: correctHeight,
              value: correctHeight,
            }
          }
          return morph
        })
      }
      return morphs
    })(),
    paginaPdf,
    hinge: bisagra,
    ArmazonBlanco: armazonBlanco, // <-- mapeo correcto
    idcambioarmazon: idCambioArmazon, // <-- mapeo correcto
    idcambiogolablanco: idCambioGolaBlanco, // <-- mapeo correcto
    idcambiogolapetroleo: idCambioGolaPetroleo, // <-- mapeo correcto
    regleta: backGLBInfo.regleta,
    regletaMaximo: backGLBInfo.regletaMaximo,
    regletaMinimo: backGLBInfo.regletaMinimo,
    ...rest,
  }
  if (type === 'linkedItem') {
    let bisagraBool = false
    if (typeof bisagra === 'boolean') {
      bisagraBool = bisagra
    } else if (typeof bisagra === 'string') {
      bisagraBool = bisagra.toLowerCase() === 'true'
    } else {
      bisagraBool = Boolean(bisagra)
    }
    ;(props as any).bisagra = bisagraBool
  }

  if (type === 'linkedItem') {
    ;(props as any).childs = new Set()
  }

  if (img) {
    props.image = img
  }

  if (itemInfo) {
    props = { ...props, ...itemInfo }
    if ('x' in itemInfo && typeof itemInfo.x === 'number') (props as any).x = itemInfo.x
    if ('y' in itemInfo && typeof itemInfo.y === 'number') (props as any).y = itemInfo.y
    if ('z' in itemInfo && typeof itemInfo.z === 'number') (props as any).z = itemInfo.z
    // IMPORTANTE: Asegurar que height siempre use alto del backend del nuevo item,
    // no la altura del item de referencia que podría venir en itemInfo
    if ('height' in itemInfo) {
      // Ignorar height de itemInfo, siempre usar correctHeight (alto del backend)
      ;(props as any).height = correctHeight
    }
  }

  // Para kube, las dimensiones de colisión derivadas del GLB deben prevalecer
  // siempre (también frente a width/height/thickness que pudieran venir en
  // itemInfo durante una sustitución), para que la caja coincida con el modelo.
  if (kubeCollisionDims) {
    ;(props as any).width = kubeCollisionDims.width
    ;(props as any).height = kubeCollisionDims.height
    ;(props as any).thickness = kubeCollisionDims.thickness
  }

  return props as Partial<Hole | Item | LinkedItem>
}
